import { LitElement, html, nothing } from 'lit';
import { t } from '../i18n';
import { api, apiGetDedupe } from '../api';
import { showToast } from '../toast';
import { defineZenComponent } from './register';
import {
  ACTIVE_INTEGRATION_PROVIDERS,
  GHOST_INTEGRATION_PROVIDERS,
  presentIntegrationPolicyRow,
  type IntegrationProvider,
  type WorkspaceIntegrationsPolicy,
} from '../integration-policy-logic';

const GHOST_ICONS: Record<string, string> = {
  google_calendar: 'calendar_month',
  slack: 'hub',
};

const GHOST_LABEL_KEYS: Record<string, string> = {
  google_calendar: 'integrationPolicyGhostGoogleCalendar',
  slack: 'integrationPolicyGhostSlack',
};

class ZenIntegrationPolicySection extends LitElement {
  static properties = {
    loading: { attribute: false },
    saving: { attribute: false },
    integrations: { attribute: false },
    viewerRole: { attribute: false },
    memberStatuses: { attribute: false },
  };

  declare loading: boolean;
  declare saving: string;
  declare integrations: WorkspaceIntegrationsPolicy;
  declare viewerRole: 'owner' | 'admin' | 'member' | 'guest';
  declare memberStatuses: Array<{ display_name: string; google: { state: string; visibility: string | null }; slack: { state: string; visibility: string | null } }>;

  constructor() {
    super();
    this.loading = false;
    this.saving = '';
    this.integrations = {};
    this.viewerRole = 'member';
    this.memberStatuses = [];
  }

  createRenderRoot() {
    return this;
  }

  private _onLangChange = () => this.requestUpdate();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('zen-lang-change', this._onLangChange);
    void this.load();
  }

  disconnectedCallback() {
    document.removeEventListener('zen-lang-change', this._onLangChange);
  }

  private get isOwner(): boolean {
    return this.viewerRole === 'owner';
  }

  private async load() {
    this.loading = true;
    this.requestUpdate();
    try {
      // #1473 Phase B swap 後: workspace_settings.sync_rules.integrations を GET /api/workspaces/me
      // 経由で読む（旧 proxy: user_profiles.sync_rules.integrations は廃止）。
      const data = await api('/api/workspaces/me');
      this.integrations = (data?.settings?.sync_rules?.integrations as WorkspaceIntegrationsPolicy) ?? {};
      this.viewerRole = data?.viewer_membership?.role ?? 'member';
      if (this.viewerRole === 'owner' || this.viewerRole === 'admin') {
        const statusData = await api('/api/workspaces/me/connection-status');
        this.memberStatuses = statusData?.members ?? [];
      }
    } catch {
      showToast(t('integrationPolicyLoadError') as string, 'error');
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  private async patch(provider: IntegrationProvider, patch: { allowed?: boolean }) {
    this.saving = provider;
    this.requestUpdate();
    try {
      const data = await api('/api/workspaces/me/settings', {
        method: 'PATCH',
        json: { integrations: { [provider]: patch } },
      });
      this.integrations = (data?.workspace?.sync_rules?.integrations as WorkspaceIntegrationsPolicy) ?? this.integrations;
      showToast(t('integrationPolicySaveSuccess') as string, 'success');
    } catch {
      showToast(t('integrationPolicySaveError') as string, 'error');
    } finally {
      this.saving = '';
      this.requestUpdate();
    }
  }

  private renderActiveRow(provider: IntegrationProvider) {
    const row = presentIntegrationPolicyRow(provider, this.integrations);
    const disabled = !this.isOwner || this.saving === provider;
    return html`
      <div class="integration-policy-row">
        <div class="provider-mark"><span class="mi">${row.icon}</span></div>
        <div class="member-main">
          <div class="member-name">${row.providerLabel}</div>
          <div class="member-sub">${t('integrationPolicyRowSub', { provider: row.providerLabel })}</div>
          <div class="connection-row-controls">
            <label class="connection-row-control">
              <span class="connection-row-control-label">${t('integrationPolicyAllowLabel')}</span>
              <button
                type="button"
                class="integration-policy-switch"
                role="switch"
                aria-checked=${row.allowed}
                aria-label=${t('integrationPolicyAllowAriaLabel', { provider: row.providerLabel })}
                ?disabled=${disabled}
                title=${!this.isOwner ? (t('integrationPolicyMemberReadOnlyHint') as string) : ''}
                @click=${() => this.patch(provider, { allowed: !row.allowed })}
              >
                <span class="switch-thumb"></span>
              </button>
            </label>
          </div>
        </div>
      </div>
    `;
  }

  private renderGhostRow(provider: string) {
    return html`
      <div class="integration-policy-ghost-row">
        <span class="mi">${GHOST_ICONS[provider] ?? 'hub'}</span>
        <span>${t(GHOST_LABEL_KEYS[provider] ?? '')}</span>
      </div>
    `;
  }

  private statusPresentation(state: string): { key: string; icon: string; tone: string } {
    if (state === 'connected') return { key: 'integrationMemberStatusConnected', icon: 'check_circle', tone: 'success' };
    if (state === 'paused') return { key: 'integrationMemberStatusPaused', icon: 'pause_circle', tone: 'warning' };
    if (state === 'reauth_required') return { key: 'integrationMemberStatusReauth', icon: 'error', tone: 'danger' };
    return { key: 'integrationMemberStatusNotConnected', icon: 'radio_button_unchecked', tone: 'muted' };
  }

  private renderMemberProvider(provider: 'Google' | 'Slack', status: { state: string; visibility: string | null }) {
    const presentation = this.statusPresentation(status.state);
    const visibility = status.visibility === 'private' ? t('connectionRowVisibilityPrivate')
      : status.visibility === 'shared' ? t('connectionRowVisibilityShared')
        : status.visibility === 'mixed' ? t('integrationMemberVisibilityMixed') : '';
    return html`<div class=${`member-sub my-connections-status ${presentation.tone}`}>
      <span class="mi mi-xs" aria-hidden="true">${presentation.icon}</span>
      <span>${provider}: ${t(presentation.key)}${visibility ? ` (${visibility})` : ''}</span>
    </div>`;
  }

  render() {
    if (this.loading) {
      return html`<div class="settings-section-placeholder">${t('integrationPolicyLoading')}</div>`;
    }

    return html`
      <div class="group-note">
        <span class="mi">info</span>
        <span>${t('integrationPolicyNote')}</span>
      </div>
      <div class="integration-policy-rows">
        ${ACTIVE_INTEGRATION_PROVIDERS.map((provider) => this.renderActiveRow(provider))}
        ${GHOST_INTEGRATION_PROVIDERS.map((provider) => this.renderGhostRow(provider))}
      </div>
      ${(this.viewerRole === 'owner' || this.viewerRole === 'admin') && this.memberStatuses.length > 0 ? html`
        <div class="integration-policy-member-status" aria-label=${t('integrationMemberStatusHeading')}>
          <h4>${t('integrationMemberStatusHeading')}</h4>
          ${this.memberStatuses.map((member) => html`
            <div class="integration-policy-row">
              <div class="member-main">
                <div class="member-name">${member.display_name}</div>
                ${this.renderMemberProvider('Google', member.google)}
                ${this.renderMemberProvider('Slack', member.slack)}
              </div>
            </div>
          `)}
        </div>
      ` : nothing}
      ${!this.isOwner
        ? html`
          <div class="member-only group-note">
            <span class="mi">info</span>
            <span>${t('integrationPolicyMemberNote')}</span>
          </div>
        `
        : nothing}
      <div class="group-note">
        <span class="mi">key</span>
        <span>${t('integrationPolicyCrossLinkNote')}</span>
      </div>
    `;
  }
}

defineZenComponent('zen-integration-policy-section', ZenIntegrationPolicySection);
